// The one tick-removal procedure on the site.
//
// Before this module the same six-to-seven steps were hand-copied into
// tick-bite-tool.tsx, tick-bite-guide.tsx (twice on one page), articles.ts and several
// species pages, and the wording had drifted apart — two strings were byte-identical
// while a third told people something the others left out. One of the copies carried
// the CDC's guidance about broken-off mouthparts and none of the pages a bitten person
// actually lands on did.
//
// Everything here restates https://www.cdc.gov/ticks/removing-a-tick/index.html, cited
// as `cdc-remove-tick`. Presentation (icons, layout, JSON-LD) belongs to the callers;
// this module stays free of React so it can be imported anywhere without cost.

export interface RemovalStep {
  /** Stable id — used for icon lookup, step anchors and analytics. */
  id: string;
  title: string;
  body: string;
  /** The mistake people make at this step, when there is a well-documented one. */
  avoid?: string;
  sourceIds: string[];
}

export const REMOVAL_STEPS: readonly RemovalStep[] = [
  {
    id: "grasp",
    title: "Grasp it as close to the skin as you can",
    body: "Use clean, fine-tipped tweezers and take hold of the tick right where it enters the skin, by the mouthparts rather than the body.",
    avoid: "Don't squeeze the swollen body. That can push more of its fluids into the bite.",
    sourceIds: ["cdc-remove-tick"],
  },
  {
    id: "pull",
    title: "Pull straight up, slowly and evenly",
    body: "Steady, even pressure, straight away from the skin. It may take a few seconds of firm pull before it lets go.",
    avoid: "Don't twist or jerk. That is what snaps the mouthparts off in the skin.",
    sourceIds: ["cdc-remove-tick"],
  },
  {
    id: "mouthparts",
    title: "If part of it stays behind, that's usually fine",
    body: "Try to lift the remaining mouthparts out with the tweezers. If they don't come away easily, leave them alone and let the skin heal. Digging after them does more damage than they do.",
    sourceIds: ["cdc-remove-tick"],
  },
  {
    id: "clean",
    title: "Clean the bite and your hands",
    body: "Wash both with soap and water, or wipe with rubbing alcohol.",
    sourceIds: ["cdc-remove-tick"],
  },
  {
    id: "keep",
    title: "Keep the tick, or photograph it",
    body: "Seal it in a bag or take a clear, close photo next to something for scale. If symptoms show up weeks from now, knowing which tick it was is useful to a clinician.",
    sourceIds: ["cdc-remove-tick", "cdc-tickborne"],
  },
  {
    id: "dispose",
    title: "Get rid of a live tick safely",
    body: "Put it in alcohol, seal it in a bag or container, wrap it tightly in tape, or flush it.",
    avoid: "Never crush a tick with your fingers.",
    sourceIds: ["cdc-remove-tick"],
  },
];

/**
 * Folk remedies the CDC names and tells people not to use. Worth stating plainly:
 * these are the methods someone is most likely to have been told by a relative, and
 * each of them makes an attached tick more likely to release fluid into the bite.
 */
export const REMOVAL_FOLK_REMEDIES: readonly string[] = [
  "Painting it with nail polish",
  "Smothering it with petroleum jelly",
  "Holding a hot match or lighter to it",
  "Waiting for it to detach on its own",
];

export const REMOVAL_TOOLS: readonly string[] = [
  "Fine-tipped tweezers (not the flat, blunt household kind)",
  "Soap and water, or rubbing alcohol",
  "A sealable bag or container if you want to keep the tick",
];

export const REMOVAL_SOURCE_IDS: readonly string[] = [
  ...new Set(REMOVAL_STEPS.flatMap((s) => s.sourceIds)),
];

/** Shape the steps for `howToJsonLd()` in ./jsonld — one HowTo definition, site-wide. */
export function removalHowToSteps(): { name: string; text: string }[] {
  return REMOVAL_STEPS.map((s) => ({
    name: s.title,
    text: s.avoid ? `${s.body} ${s.avoid}` : s.body,
  }));
}
